import { useEffect, useState } from 'react';
import { useUi } from '@/lib/store';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import { ArrowRight, Check, ClipboardCopy, Download, KeyRound, ShieldCheck, Smartphone } from 'lucide-react';
import type { MfaChallenge } from '@dts/core';
import type { PublicSession } from '@/bridge/types';
import { Button, Input } from './ui';
import { MfaQrCode } from './MfaQrCode';

/**
 * Second sign-in step. "verify": enter the code from the authenticator app (or a recovery code).
 * "setup": scan the QR code, confirm a code, then save the one-time recovery codes.
 */
export function MfaStep({ server, challenge, onDone, onCancel }: { server: string; challenge: MfaChallenge; onDone: (s: PublicSession) => void; onCancel: () => void }) {
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ session: PublicSession; codes: string[] } | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  // Hold back an update restart while a QR code is on screen or recovery codes haven't been saved yet.
  const setSignInBusy = useUi((s) => s.setSignInBusy);
  useEffect(() => {
    if (challenge.step !== 'setup') return;
    setSignInBusy(true);
    return () => setSignInBusy(false);
  }, [challenge.step, setSignInBusy]);

  const submit = async (value = code) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    if (challenge.step === 'setup') {
      const r = await window.dts.auth.enableMfa(server, challenge.mfaToken, value);
      setBusy(false);
      if (!r.ok) return fail(r.error);
      setSaved({ session: r.data.session, codes: r.data.recoveryCodes });
    } else {
      const r = await window.dts.auth.verifyMfa(server, challenge.mfaToken, value);
      setBusy(false);
      if (!r.ok) return fail(r.error);
      onDone(r.data);
    }
  };
  const fail = (message: string) => {
    setError(message);
    setCode('');
  };
  const typeCode = (v: string) => {
    if (useRecovery) return setCode(v.toLowerCase());
    const digits = v.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    if (digits.length === 6) void submit(digits); // no need to press Enter
  };

  if (saved) return <RecoveryCodes codes={saved.codes} confirmed={confirmed} setConfirmed={setConfirmed} onContinue={() => onDone(saved.session)} />;

  const setup = challenge.step === 'setup' ? challenge.setup : undefined;
  return (
    <form onSubmit={(e) => (e.preventDefault(), void submit())}>
      <div className="mb-5">
        <div className="flex items-center gap-2 text-[22px] font-semibold tracking-tight"><ShieldCheck className="size-5 text-accent" />Two-step verification</div>
        <div className="mt-0.5 text-[13px] text-muted">
          {setup ? 'Required for every account. It takes about a minute.' : useRecovery ? 'Enter one of the recovery codes you saved when you set this up.' : 'Enter the 6-digit code from your authenticator app.'}
        </div>
      </div>

      {setup && (
        <ol className="mb-4 space-y-3 text-[12.5px] text-muted">
          <li className="flex gap-2"><Smartphone className="mt-0.5 size-4 shrink-0 text-accent" />Open an authenticator app on your phone, such as Microsoft Authenticator, Google Authenticator, Authy or 1Password.</li>
          <li>
            <div className="mb-2">Add an account and scan this code:</div>
            <div className="flex justify-center"><MfaQrCode value={setup.otpAuthUri} /></div>
            <details className="mt-2 text-[12px]">
              <summary className="cursor-pointer text-accent">Can't scan it? Enter a key instead</summary>
              <div className="mt-1.5 flex items-center gap-2 rounded-xl bg-bg/60 px-3 py-2">
                <code className="selectable flex-1 break-all font-mono text-[12px] tracking-wider text-fg">{setup.secret.match(/.{1,4}/g)?.join(' ')}</code>
                <button type="button" title="Copy key" className="text-faint hover:text-fg" onClick={() => (navigator.clipboard.writeText(setup.secret), toast.success('Key copied'))}><ClipboardCopy className="size-4" /></button>
              </div>
              <div className="mt-1 text-faint">Account: {setup.account} · Type: time-based</div>
            </details>
          </li>
          <li>Then enter the 6-digit code the app shows:</li>
        </ol>
      )}

      <Input
        autoFocus
        required
        value={code}
        onChange={(e) => typeCode(e.target.value)}
        placeholder={useRecovery ? 'xxxxx-xxxxx' : '123456'}
        inputMode={useRecovery ? 'text' : 'numeric'}
        autoComplete="one-time-code"
        className="h-12 text-center font-mono text-[22px] tracking-[0.4em]"
      />
      {error && <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="mt-3 rounded-xl bg-danger/10 px-3 py-2 text-[12.5px] text-danger">{error}</motion.div>}
      <Button type="submit" variant="primary" size="lg" loading={busy} disabled={useRecovery ? code.replace(/[^a-z0-9]/g, '').length !== 10 : code.length !== 6} className="mt-5 w-full">
        {setup ? 'Turn on and sign in' : 'Verify'} <ArrowRight className="size-4" />
      </Button>
      {!setup && (
        <button type="button" onClick={() => (setUseRecovery(!useRecovery), setCode(''), setError(null))} className="mx-auto mt-4 flex items-center gap-1.5 text-[12.5px] font-medium text-accent hover:underline">
          <KeyRound className="size-3.5" />{useRecovery ? 'Use my authenticator app' : 'Lost your phone? Use a recovery code'}
        </button>
      )}
      <button type="button" onClick={onCancel} className="mx-auto mt-3 block text-[12px] text-faint hover:text-muted">Use a different account</button>
    </form>
  );
}

/** Shown once, right after setup: codes that each sign in once if the phone is lost. */
function RecoveryCodes({ codes, confirmed, setConfirmed, onContinue }: { codes: string[]; confirmed: boolean; setConfirmed: (v: boolean) => void; onContinue: () => void }) {
  const text = `DTS Mail recovery codes\nEach code works once, in place of your authenticator app.\n\n${codes.join('\n')}\n`;
  return (
    <div>
      <div className="mb-4">
        <div className="flex items-center gap-2 text-[22px] font-semibold tracking-tight"><Check className="size-5 text-ok" />You're set up</div>
        <div className="mt-0.5 text-[13px] text-muted">Save these recovery codes somewhere safe, such as a password manager. Each one signs you in once if you lose your phone. This is the only time they're shown.</div>
      </div>
      <div className="selectable grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-2xl bg-bg/60 px-4 py-3 font-mono text-[14px] tracking-wider">
        {codes.map((c) => <span key={c}>{c}</span>)}
      </div>
      <div className="mt-3 flex gap-2">
        <Button type="button" variant="soft" size="sm" icon={<ClipboardCopy className="size-3.5" />} onClick={() => (navigator.clipboard.writeText(text), toast.success('Recovery codes copied'))}>Copy</Button>
        <Button type="button" variant="soft" size="sm" icon={<Download className="size-3.5" />} onClick={() => void window.dts.files.saveText('dts-mail-recovery-codes.txt', text)}>Download</Button>
      </div>
      <label className="mt-4 flex items-center gap-2 text-[13px]"><input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} /> I've saved my recovery codes</label>
      <Button type="button" variant="primary" size="lg" disabled={!confirmed} onClick={onContinue} className="mt-4 w-full">Continue to DTS Mail <ArrowRight className="size-4" /></Button>
    </div>
  );
}
